import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'

interface ConfirmDialogProps {
  open: boolean
  title: string
  text: string
  confirmLabel?: string
  danger?: boolean
  doubleConfirm?: boolean
  /** Akce právě běží — blokuje zavření i opakované odeslání. */
  submitting?: boolean
  /** Chyba akce; zobrazuje se UVNITŘ dialogu, jinak by ji překryl backdrop. */
  error?: string | null
  onConfirm: () => void
  onClose: () => void
}

/**
 * Potvrzovací dialog. Při `doubleConfirm` vyžaduje dva kliky: první přepne dialog
 * do varovného kroku (nevratná akce), teprve druhý volá `onConfirm`.
 */
export function ConfirmDialog({
  open,
  title,
  text,
  confirmLabel = 'Potvrdit',
  danger = false,
  doubleConfirm = false,
  submitting = false,
  error = null,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  // Druhý krok dvojitého potvrzení; resetuje se při každém otevření.
  const [secondStep, setSecondStep] = useState(false)

  useEffect(() => {
    if (open) {
      setSecondStep(false)
    }
  }, [open])

  const handleConfirm = () => {
    if (submitting) return
    if (doubleConfirm && !secondStep) {
      setSecondStep(true)
      return
    }
    onConfirm()
  }

  const confirmColor = danger || secondStep ? 'error' : 'primary'
  const confirmText = secondStep ? `Ano, opravdu ${confirmLabel}` : confirmLabel

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {secondStep ? (
          <DialogContentText sx={{ color: 'error.main' }}>
            Opravdu? Tato akce je nevratná. {text}
          </DialogContentText>
        ) : (
          <DialogContentText>{text}</DialogContentText>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" color="inherit" onClick={onClose} disabled={submitting}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={handleConfirm}
          disabled={submitting}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
